<template>
    <div class="flex justify-center">
        <VcPopover
            trigger="click"
            :disabled="!currentAgentsCount"
            popover-class="redesign-popover"
            teleported
            @show="open = true"
            @hide="open = false"
        >
            <template #reference>
                <div :class="{ 'cursor-pointer': currentAgentsCount }">
                    {{ currentAgentsCount }}
                </div>
            </template>
            <RedesignCappedAgentList
                v-if="open"
                :items="items"
                :total-count="currentAgentsCount"
            />
        </VcPopover>
    </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { VcPopover } from '@voicenter-team/voicenter-ui-plus'
import RedesignCappedAgentList from '@/components/RedesignBaseComponents/RedesignCappedAgentList.vue'
import { allMembersMap, ProcessedQueues } from '@/composables/useQueueAgentsComposable'
import { ALL_QUEUE_ID } from '@/enum/redesignQueueDashboardStatistics'

import { CAPPED_AGENT_LIST_MAX_VISIBLE } from '@/components/RedesignBaseComponents/cappedAgentListConfig'

const props = defineProps<{
    queueId: number | string
    available: boolean
    agents: ProcessedQueues
}>()

const open = ref(false)

const queueMembers = computed(() => {
    return allMembersMap?.value?.[props.queueId]?.Members ?? {}
})

const queueAvailable = computed(() => {
    if (props.queueId === ALL_QUEUE_ID) {
        return props.agents.allAvailable
    }
    return props.agents.allAvailable.filter(agent => {
        return !!queueMembers.value[agent.UserID]
    })
})

const unavailableAgentsCount = computed(() => {
    if (props.queueId === ALL_QUEUE_ID) {
        return props.agents.allUnavailable.length
    }
    return Object.values(queueMembers.value).length - queueAvailable.value.length
})

const currentAgentsCount = computed(() => {
    return props.available ? queueAvailable.value.length : unavailableAgentsCount.value
})

const getUnavailable = () => {
    const result = []

    for (const agent of props.agents.allUnavailable) {
        if (queueMembers.value[agent.UserID] || props.queueId === ALL_QUEUE_ID) {
            result.push(agent)
            if (result.length === CAPPED_AGENT_LIST_MAX_VISIBLE) break
        }
    }

    return result
}

const items = computed(() => {
    if (open.value && props.available) {
        return queueAvailable.value
            .slice(0, CAPPED_AGENT_LIST_MAX_VISIBLE)
            .map(toListItem)
    }
    if (open.value && !props.available) {
        return getUnavailable().map(toListItem)
    }
    return currentAgentsCount.value
})

function toListItem (agent: { UserID: number, UserName: string }) {
    return {
        id: agent.UserID,
        name: agent.UserName
    }
}
</script>
